import { Form, Radio, Row } from 'antd';
import { RadioChangeEvent } from 'antd/lib/radio';
import { UploadChangeParam, UploadFile } from 'antd/lib/upload/interface';
import dayjs from 'dayjs';
import React, { useContext, useEffect, useState } from 'react';
import { IAmlz, ISubj, ISubjPrilozenesoubory, Ic4 } from 'types/amlz';

import AnchorComponent from 'components/atoms/AnchorComponent';
import ButtonComponent from 'components/atoms/ButtonComponent';
import InputTextArea from 'components/atoms/InputTextArea';
import Paragraph from 'components/atoms/Paragraph';
import TableComponent from 'components/atoms/TableComponent';
import Title from 'components/atoms/Title';
import UploadComponent, { IFileList } from 'components/atoms/UploadComponent';
import { Context } from 'components/hoc/Store';
import TranslateText from 'components/hoc/TranslateText';
import FormWrapper from 'components/molecules/FormWrapper';

import { IcisStaty } from 'services/cisStaty';
import fileDownload from 'services/fileDownload';
import { prepareFileAmlz } from 'services/prepareFile';
import { EEsmStav, IOsobyData } from 'services/spravaEmitenta/C4osoby';

import { ETypSouboru } from 'enums/amlz/typSouboru';
import { EUdajeOFyzOsobach } from 'enums/amlz/udajeOFyzOsobach';
import { ENotificationType } from 'enums/notificationType';

import nepovoleneZnakyValidation from 'utils/validations/nepovoleneZnakyValidation';

import C4ApridatOsobu from './C4ApridatOsobu';
import C4BpridatOsobu from './C4BpridatOsobu';
import C4duvodOdstraneni from './C4duvodOdstraneni';

const radioStyle = {
    display: 'block',
    paddingTop: 6,
    paddingBottom: 6,
    whiteSpace: 'normal' as const
};

const pravniFormavals = {
    allOptions: [121],
    CuploadNotReq: [141, 331, 921, 922],
    Conly: [
        145, 301, 302, 313, 314, 325, 332, 352, 361, 382, 525, 601, 641, 651, 661, 671, 703, 707, 711, 715, 721, 722,
        723, 732, 733, 741, 745, 761, 771, 801, 804, 805, 811, 941, 960
    ]
};

interface IC4 {
    C4data?: Ic4;
    countries?: IcisStaty[];
    prevStep: () => void;
    nextStep: () => void;
    SubmitFunc: <T extends keyof IAmlz['amlz']>(key: T, values: IAmlz['amlz'][T]) => void;
    isC6edit: string;
    C4apiFinished: boolean;
    osobyResp?: IOsobyData;
    c1kodpravniformy: string;
    idZadosti: number | string;
    setSaving: React.Dispatch<React.SetStateAction<boolean>>;
    isLegalEntity: boolean;
}

const C4: React.FC<IC4> = ({
    C4data,
    countries,
    prevStep,
    nextStep,
    SubmitFunc,
    isC6edit,
    c1kodpravniformy,
    setSaving,
    idZadosti,
    C4apiFinished,
    osobyResp,
    isLegalEntity
}) => {
    const { NotificationComponent } = useContext(Context);
    const [form] = Form.useForm();
    const [personDataOdstraneni, setPersonDataOdstraneni] = useState<ISubj | undefined>();
    const [personData, setPersonData] = useState<ISubj | undefined>();
    const [submitPersonData, setSubmitPersonData] = useState<ISubj | null>();
    const [subjIndex, setSubjIndex] = useState<number>(-1);
    const [tableData, setTableData] = useState<ISubj[]>([]);
    const [deletePerson, setDeletePerson] = useState<number>(-1);
    const [emitentValue, setEmitentValue] = useState<EUdajeOFyzOsobach | undefined>(C4data?.empro);
    const [uploading, setUploading] = useState<boolean>();
    const [fileList, setFileList] = useState<IFileList[] | undefined>(
        C4data?.prilozenesoubory?.map((e, i) => {
            return {
                uid: String(i),
                name: e.orignazev ? e.orignazev : 'bez nazvu',
                fileName: e.tempnazev ? e.tempnazev : 'a',
                type: e.orignazev ? e.orignazev.split('.').slice(-1)[0] : '',
                size: 0,
                status: 'done'
            };
        })
    );
    const [prilozeneSoubory, setPrilozeneSoubory] = useState<ISubjPrilozenesoubory[]>(C4data?.prilozenesoubory || []);
    const allOptionsAvailable = pravniFormavals.allOptions.includes(Number(c1kodpravniformy?.substring(2)));
    const cOnlySouborRequired = pravniFormavals.Conly.includes(Number(c1kodpravniformy?.substring(2)));
    const cOnly = [...pravniFormavals.CuploadNotReq, ...pravniFormavals.Conly].includes(
        Number(c1kodpravniformy?.substring(2))
    );

    const uploadProps = {
        buttonLoading: uploading,
        showDownloadIcon: true,
        onChange: (data: UploadChangeParam) => {
            if (data.file.status === 'uploading' && setUploading) setUploading(true);
            if (data.file.status !== 'uploading' && setUploading) setUploading(false);
            if (data.fileList.length) {
                if (data.file.response) {
                    setPrilozeneSoubory(
                        data.fileList
                            .filter(f => f.status && f.status !== 'error')
                            .map(e => {
                                return {
                                    orignazev: e.name.normalize(),
                                    tempnazev: e.response ? e.response.tempnazev : e.fileName,
                                    oblast: ETypSouboru.SOUBOR_DOKLAD
                                };
                            })
                    );
                    form.validateFields();
                }

                setFileList(
                    data.fileList
                        .filter(f => f.status && f.status !== 'error')
                        .map(e => {
                            return {
                                uid: e.uid,
                                name: e.name.normalize(),
                                fileName: e.fileName ? e.fileName : e.response ? e.response.tempnazev : '',
                                type: e.type!,
                                size: e.size!,
                                status: e.status ? e.status : 'error'
                            };
                        })
                );
            }
        },
        onRemove: (file: UploadFile) => {
            if (fileList?.length) {
                const oldState = [...fileList];
                const newState = oldState.filter(e => e.uid !== file.uid);
                setFileList(newState);
                const newSoubory = [...prilozeneSoubory].filter(e => e.tempnazev !== file.fileName);
                setPrilozeneSoubory(newSoubory);
            }
        },
        onDownload: (file: UploadFile) => {
            setUploading(true);
            prepareFileAmlz(idZadosti, file.fileName ? file.fileName : '')
                .then(resp => {
                    fileDownload(resp.redirect, file.name ? file.name : 'file');
                })
                .finally(() => setUploading(false));
        },
        fileList: fileList,
        buttonDisabled: fileList ? fileList.length > 4 : false
    };

    const hanldeRadioChange = (e: RadioChangeEvent) => {
        setEmitentValue(e.target.value);
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const onFinish = (fieldsValue: any) => {
        const valuesOfFields = {
            ...fieldsValue
        };
        const c4Prev = {
            ...C4data
        };
        delete c4Prev.duvod;
        delete valuesOfFields.misto; // odstrani prazdny string ktory vyhadzoval chybu na backende

        if (emitentValue === EUdajeOFyzOsobach.UVEDENE) {
            SubmitFunc('c4', {
                ...c4Prev,
                empro: EUdajeOFyzOsobach.UVEDENE,
                subj: tableData,
                prilozenesoubory: prilozeneSoubory
            });
        }
        if (emitentValue === EUdajeOFyzOsobach.NEUVEDENE) {
            SubmitFunc('c4', {
                ...c4Prev,
                empro: EUdajeOFyzOsobach.NEUVEDENE,
                subj: tableData,
                prilozenesoubory: prilozeneSoubory
            });
        }
        if (emitentValue === EUdajeOFyzOsobach.NENI) {
            SubmitFunc('c4', {
                empro: EUdajeOFyzOsobach.NENI,
                duvod: fieldsValue.duvod,
                prilozenesoubory: prilozeneSoubory
            });
        }
        nextStep();
    };

    useEffect(() => {
        C4data?.empro && setEmitentValue(C4data?.empro);
        C4data?.prilozenesoubory &&
            setFileList(
                C4data?.prilozenesoubory?.map((e, i) => {
                    return {
                        uid: String(i),
                        name: e.orignazev ? e.orignazev : 'bez nazvu',
                        fileName: e.tempnazev ? e.tempnazev : 'a',
                        type: e.orignazev ? e.orignazev.split('.').slice(-1)[0] : '',
                        size: 0,
                        status: 'done'
                    };
                })
            );
        C4data?.prilozenesoubory && setPrilozeneSoubory(C4data?.prilozenesoubory || []);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [C4data?.empro, C4data?.prilozenesoubory]);

    useEffect(() => {
        if (C4apiFinished) {
            if (
                ((osobyResp?.datumnacteni &&
                    C4data?.datumnacteni &&
                    dayjs(osobyResp?.datumnacteni).format('YYYY.MM.DD') !==
                        dayjs(C4data?.datumnacteni).format('YYYY.MM.DD')) ||
                    (C4data && !C4data?.datumnacteni)) &&
                osobyResp
            ) {
                setTableData(osobyResp.osoby);
                C4data?.datumnacteni &&
                    NotificationComponent({
                        description: '',
                        title: 'C4_OSOBY_RELOAD',
                        type: ENotificationType.INFO
                    });
                setSaving(true);
                SubmitFunc('c4', {
                    ...C4data,
                    subj: osobyResp.osoby,
                    datumnacteni: osobyResp.datumnacteni
                });
            } else {
                setTableData(C4data?.subj || []);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [C4apiFinished]);

    useEffect(() => {
        // submit c4 subj
        if (submitPersonData) {
            const subjToUpdate = [...((C4data?.empro === emitentValue ? tableData : osobyResp?.osoby) || [])];
            if (subjIndex > -1) {
                subjToUpdate[subjIndex] = submitPersonData;
            } else {
                subjToUpdate.push(submitPersonData);
            }
            setSaving(true);
            SubmitFunc('c4', {
                ...C4data,
                empro: emitentValue,
                subj: subjToUpdate,
                ...(C4data?.datumnacteni && {
                    datumnacteni: C4data?.datumnacteni
                })
            });
            setTableData(subjToUpdate);
            setSubjIndex(-1);
            setPersonData(undefined);
            setPersonDataOdstraneni(undefined);
            setSubmitPersonData(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [submitPersonData]);

    useEffect(() => {
        if (deletePerson > -1) {
            const subj = JSON.parse(JSON.stringify(C4data?.subj));
            subj.splice(deletePerson, 1);
            setSaving(true);
            SubmitFunc('c4', {
                ...C4data,
                subj: subj
            });
            setTableData(subj);
            setDeletePerson(-1);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [deletePerson]);

    const columns = [
        {
            title: <TranslateText>JMENO</TranslateText>,
            dataIndex: 'jmeno',
            key: 'jmeno',
            render: (text: string, row: ISubj) => `${text ? text : ''} ${row.prijmeni ? row.prijmeni : ''}`
        },
        {
            title: <TranslateText>RODNE_CISLO_DATUM_NAR</TranslateText>,
            dataIndex: 'datumnar',
            key: 'datumnar',
            render: (text: string, row: ISubj) => (text ? dayjs(text).format('DD.MM.YYYY') : row.idmb)
        },
        {
            title: <TranslateText>FORMULARE_ADRESA</TranslateText>,
            dataIndex: 'mema',
            key: 'mema',
            render: (_: string, row: ISubj) => {
                return `${row.adma ? row.adma + ' ,' : ''} ${row.mema ? row.mema + ' ,' : ''} ${
                    row.pscm ? row.pscm + ' ,' : ''
                } ${row.kost ? row.kost : ''}`;
            }
        },
        {
            title: <TranslateText>STAV</TranslateText>,
            dataIndex: 'stavesm',
            key: 'stavesm',
            render: (stavesm: string) => <TranslateText>{`C4_ESM_STAV_${stavesm}`}</TranslateText>
        },
        {
            align: 'center' as const,
            width: 190,
            dataIndex: 'stavesm',
            key: 'stavesm',
            render: (text: EEsmStav, row: ISubj, index: number) => {
                return (
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'space-around'
                        }}
                    >
                        <AnchorComponent
                            id={`C4DoplnitBtn${index}`}
                            key={index + 'b'}
                            onClick={() => {
                                setSubjIndex(index);
                                setPersonData(row);
                            }}
                        >
                            <TranslateText>
                                {text === EEsmStav.ESMDOPL
                                    ? 'DOPLNIT'
                                    : text === EEsmStav.ESMODEB
                                      ? 'BUTTON_AKTIVURAV'
                                      : 'BUTTON_UPRAVIT'}
                            </TranslateText>
                        </AnchorComponent>

                        {text !== EEsmStav.ESMODEB && (
                            <AnchorComponent
                                id={`C4OdebratBtn${index}`}
                                key={index}
                                onClick={() =>
                                    text === EEsmStav.RUCNE
                                        ? setDeletePerson(index)
                                        : (setPersonDataOdstraneni(row), setSubjIndex(index))
                                }
                            >
                                <TranslateText>{text === EEsmStav.RUCNE ? 'SMAZAT' : 'ODEBRAT'}</TranslateText>
                            </AnchorComponent>
                        )}
                    </div>
                );
            }
        }
    ];
    return !personData && !personDataOdstraneni ? (
        <FormWrapper onFinish={onFinish} name="C4" outForm={form} preserve={false}>
            <Title
                level={4}
                text={isLegalEntity ? 'FORMULARE_AMLZ_C4_TITLE_LEGALENTITTY' : 'FORMULARE_AMLZ_C4_TITLE'}
            />
            <Paragraph
                text={'FORMULARE_AMLZ_C4_TEXT'}
                style={{
                    display: 'block',
                    paddingBottom: 20
                }}
            />
            <Paragraph
                text={'FORMULARE_AMLZ_C4_TEXT_A'}
                style={{
                    display: 'block'
                }}
            />
            <Paragraph
                text={'FORMULARE_AMLZ_C4_TEXT_A1'}
                style={{
                    display: 'block'
                }}
            />
            <Paragraph
                text={'FORMULARE_AMLZ_C4_TEXT_A2'}
                style={{
                    display: 'block'
                }}
            />
            <Paragraph
                text={'FORMULARE_AMLZ_C4_TEXT_B'}
                style={{
                    display: 'block',
                    paddingTop: 20
                }}
            />
            <Paragraph
                text={'FORMULARE_AMLZ_C4_TEXT_B1'}
                style={{
                    display: 'block'
                }}
            />
            <Paragraph
                text={'FORMULARE_AMLZ_C4_TEXT_B2'}
                style={{
                    display: 'block'
                }}
            />
            <Paragraph
                text={'FORMULARE_AMLZ_C4_TEXT_B3'}
                style={{
                    display: 'block'
                }}
            />
            <Paragraph
                text={'FORMULARE_AMLZ_C4_TEXT_C'}
                style={{
                    display: 'block',
                    paddingBottom: 20
                }}
            />
            <Paragraph
                text={'FORMULARE_AMLZ_C4_TEXT_C1'}
                style={{
                    display: 'block'
                }}
            />
            <Paragraph
                text={'FORMULARE_AMLZ_C4_TEXT_C2'}
                style={{
                    display: 'block'
                }}
            />
            <Paragraph
                text={'FORMULARE_AMLZ_C4_TEXT_C3'}
                style={{
                    display: 'block',
                    paddingBottom: 20
                }}
            />
            <Paragraph
                text={'FORMULARE_AMLZ_C4_TEXT_C4'}
                style={{
                    display: 'block',
                    paddingBottom: 20
                }}
            />
            <Paragraph
                text={'FORMULARE_AMLZ_C4_TEXT_C5'}
                style={{
                    display: 'block',
                    paddingBottom: 20
                }}
            />
            <Paragraph
                text={'FORMULARE_AMLZ_C4_TEXT_C6'}
                style={{
                    display: 'block',
                    paddingBottom: 20
                }}
            />
            {tableData && (
                <>
                    <Radio.Group onChange={hanldeRadioChange} value={emitentValue}>
                        <Radio style={radioStyle} value={EUdajeOFyzOsobach.UVEDENE}>
                            <TranslateText>FORMULARE_AMLZ_C4_RADIO_UV</TranslateText>
                        </Radio>
                        <Radio style={radioStyle} value={EUdajeOFyzOsobach.NEUVEDENE}>
                            <TranslateText>FORMULARE_AMLZ_C4_RADIO_NEUV</TranslateText>
                        </Radio>
                        <Radio style={radioStyle} value={EUdajeOFyzOsobach.NENI}>
                            <TranslateText>FORMULARE_AMLZ_C4_RADIO_NENI</TranslateText>
                        </Radio>
                    </Radio.Group>
                    {(emitentValue === EUdajeOFyzOsobach.UVEDENE || emitentValue === EUdajeOFyzOsobach.NEUVEDENE) &&
                    cOnly ? (
                        <Paragraph text={'C4_C_PRAVNIFORMA_ERR'} className="extramessage-text" />
                    ) : !cOnly && emitentValue === EUdajeOFyzOsobach.NENI && !allOptionsAvailable ? (
                        <Paragraph text={'C4_AB_PRAVNIFORMA_ERR'} className="extramessage-text" />
                    ) : (
                        emitentValue &&
                        emitentValue !== EUdajeOFyzOsobach.NENI && (
                            <>
                                <Title
                                    level={5}
                                    text={
                                        emitentValue === EUdajeOFyzOsobach.UVEDENE
                                            ? 'FORMULARE_SKUTECNI_MAJITELE'
                                            : 'FORMULARE_OSOBY'
                                    }
                                    style={{
                                        margin: 0,
                                        marginTop: 10
                                    }}
                                />
                                <TableComponent
                                    locale={{
                                        emptyText: <TranslateText>ZADNE_UDAJE</TranslateText>
                                    }}
                                    rowClassName={record =>
                                        record.stavesm === EEsmStav.ESMODEB
                                            ? 'tableRowGray'
                                            : record.stavesm === EEsmStav.RUCNE
                                              ? 'tableRowGreen'
                                              : record.stavesm === EEsmStav.ESMDOPL ||
                                                  (record.stat === 'CZ' && !record.idmb)
                                                ? 'tableRowRed'
                                                : ''
                                    }
                                    style={{
                                        margin: '30px 0px'
                                    }}
                                    rowKey={(_, index) => index || 0}
                                    columns={columns}
                                    dataSource={C4data?.empro === emitentValue ? tableData : osobyResp?.osoby}
                                    pagination={false}
                                    footer={() => (
                                        <AnchorComponent
                                            id="C4PridatOsobu"
                                            style={{
                                                textAlign: 'center',
                                                display: 'block'
                                            }}
                                            onClick={() => {
                                                setPersonData({});
                                                setSubjIndex(-1);
                                            }}
                                        >
                                            <TranslateText>PRIDAT_OSOBU</TranslateText>
                                        </AnchorComponent>
                                    )}
                                />
                            </>
                        )
                    )}

                    {emitentValue === EUdajeOFyzOsobach.NENI &&
                        (osobyResp?.osoby.length ? (
                            <Paragraph text="C4_C_ERR" className="extramessage-text" />
                        ) : (
                            <>
                                <InputTextArea
                                    rows={4}
                                    name="duvod"
                                    initialValue={C4data?.duvod ? C4data.duvod : ''}
                                    label="DUVOD"
                                    notRequired={cOnly}
                                    rules={nepovoleneZnakyValidation()}
                                />
                            </>
                        ))}
                    <Form.Item
                        style={{
                            marginTop: 16,
                            marginBottom: 16
                        }}
                        label={<Paragraph text={'VLOZTE_DOKUMENT'} strong={true} />}
                        rules={[
                            {
                                required: cOnlySouborRequired,
                                validator() {
                                    try {
                                        if (cOnlySouborRequired && (!fileList || fileList.length < 1))
                                            return Promise.reject(
                                                <TranslateText>NEVALIDNI_POVINNA_POLOZKA_UPLOAD</TranslateText>
                                            );
                                        return Promise.resolve();
                                    } catch {
                                        return Promise.reject();
                                    }
                                }
                            }
                        ]}
                        validateTrigger="onChange"
                    >
                        <div
                            style={{
                                marginBottom: 22
                            }}
                        >
                            <UploadComponent {...uploadProps} id="c4UploadBtn" />
                        </div>
                    </Form.Item>

                    <Row className="button-row">
                        {!isC6edit && (
                            <ButtonComponent
                                buttontext="ZPET"
                                onClick={() => prevStep()}
                                id="C4ZpetBtn"
                                type="default"
                            />
                        )}
                        <ButtonComponent
                            buttontext="POKRACOVAT"
                            id={isC6edit ? 'C4PrehledBtn' : 'C4PokracovatBtn'}
                            disabled={
                                (!cOnly && !allOptionsAvailable && emitentValue === EUdajeOFyzOsobach.NENI) ||
                                (emitentValue !== EUdajeOFyzOsobach.NENI && tableData
                                    ? tableData.filter(e => e.stavesm === EEsmStav.ESMDOPL).length > 0 ||
                                      tableData.filter(
                                          e => e.stavesm === EEsmStav.RUCNE || e.stavesm === EEsmStav.ESMOK
                                      ).length < 1 ||
                                      tableData.filter(record => record.stat === 'CZ' && !record.idmb).length > 0 ||
                                      C4data?.empro !== emitentValue
                                    : false || (osobyResp?.osoby || []).length > 0)
                            }
                            loading={uploading}
                            type="primary"
                            onClick={() => form.submit()}
                        />
                    </Row>
                </>
            )}
        </FormWrapper>
    ) : personDataOdstraneni ? (
        <C4duvodOdstraneni
            personData={personDataOdstraneni}
            setPersonDataOdstraneni={setPersonDataOdstraneni}
            setSubmitPersonData={setSubmitPersonData}
            idZadosti={idZadosti}
            osobyResp={osobyResp}
        />
    ) : emitentValue === EUdajeOFyzOsobach.UVEDENE && personData ? (
        <C4ApridatOsobu
            setSubmitPersonData={setSubmitPersonData}
            personData={personData}
            setPersonData={setPersonData}
            countries={countries}
            subjIndex={subjIndex}
            osobyResp={osobyResp}
            idZadosti={idZadosti}
        />
    ) : emitentValue === EUdajeOFyzOsobach.NEUVEDENE && personData ? (
        <C4BpridatOsobu
            setSubmitPersonData={setSubmitPersonData}
            personData={personData}
            setPersonData={setPersonData}
            countries={countries}
            subjIndex={subjIndex}
            osobyResp={osobyResp}
            idZadosti={idZadosti}
        />
    ) : null;
};

export default C4;
